import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useLang } from "../context/LanguageContext";
import { Menu, X } from "lucide-react";

export default function Navbar() {
  const { user, logout } = useAuth();
  const { lang, setLang, t } = useLang();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  const handleLogout = async () => {
    await logout();
    setOpen(false);
    navigate("/");
  };

  const linkClass =
    "text-sm font-semibold text-[#334155] hover:text-[#1D4ED8] px-2 2xl:px-3 py-2 rounded-full hover:bg-[#F8FAFC] transition-colors duration-200 whitespace-nowrap";

  const links = (
    <>
      <Link data-testid="nav-diplomas-link" to="/diplomas" onClick={() => setOpen(false)} className={linkClass}>
        {t("nav.diplomas")}
      </Link>
      <Link data-testid="nav-courses-link" to="/courses" onClick={() => setOpen(false)} className={linkClass}>
        {t("nav.courses")}
      </Link>
      <Link data-testid="nav-articles-link" to="/articles" onClick={() => setOpen(false)} className={linkClass}>
        {t("nav.articles")}
      </Link>
      {user && (
        <Link data-testid="nav-learn-link" to="/courses?tab=online" onClick={() => setOpen(false)} className={`${linkClass} xl:hidden`}>
          {t("nav.academy")}
        </Link>
      )}
      {user && (
        <Link data-testid="nav-certificates-link" to="/certificates" onClick={() => setOpen(false)} className={linkClass}>
          {t("nav.certificates")}
        </Link>
      )}
      <Link data-testid="nav-mentorship-link" to="/mentorship" onClick={() => setOpen(false)} className={linkClass}>
        {t("nav.mentorship")}
      </Link>
      <Link data-testid="nav-b2b-link" to="/b2b" onClick={() => setOpen(false)} className={linkClass}>
        {t("nav.b2b")}
      </Link>
      {user && (
        <Link data-testid="nav-leaderboard-link" to="/leaderboard" onClick={() => setOpen(false)} className={`${linkClass} xl:hidden`}>
          {t("nav.leaderboard")}
        </Link>
      )}
      {user && (
        <Link data-testid="nav-dashboard-link" to="/dashboard" onClick={() => setOpen(false)} className={`${linkClass} xl:hidden`}>
          {t("nav.dashboard")}
        </Link>
      )}
      {user && user.is_manager && (
        <Link data-testid="nav-team-link" to="/team" onClick={() => setOpen(false)} className={linkClass}>
          {t("nav.team")}
        </Link>
      )}
      {user && user.role === "admin" && (
        <Link data-testid="nav-admin-link" to="/admin" onClick={() => setOpen(false)} className={`${linkClass} xl:hidden`}>
          {t("nav.admin")}
        </Link>
      )}
      {user && user.role === "admin" && (
        <Link data-testid="nav-studio-link" to="/studio" onClick={() => setOpen(false)} className={`${linkClass} xl:hidden`}>
          Studio
        </Link>
      )}
    </>
  );

  const adminLinks = user && user.role === "admin" && (
    <>
      <Link data-testid="nav-admin-link-desktop" to="/admin" className={linkClass}>
        {t("nav.admin")}
      </Link>
      <Link data-testid="nav-studio-link-desktop" to="/studio" className={linkClass}>
        Studio
      </Link>
    </>
  );

  const langToggle = (suffix = "") => (
    <div className="flex items-center bg-[#F8FAFC] border border-[#E2E8F0] rounded-full p-0.5">
      <button
        data-testid={`lang-toggle-el${suffix}`}
        onClick={() => setLang("el")}
        className={`px-3 py-1 text-[11px] font-bold rounded-full transition-colors duration-200 ${lang === "el" ? "bg-[#1D4ED8] text-white" : "text-[#334155] hover:text-[#1D4ED8]"}`}
      >
        EL
      </button>
      <button
        data-testid={`lang-toggle-en${suffix}`}
        onClick={() => setLang("en")}
        className={`px-3 py-1 text-[11px] font-bold rounded-full transition-colors duration-200 ${lang === "en" ? "bg-[#1D4ED8] text-white" : "text-[#334155] hover:text-[#1D4ED8]"}`}
      >
        EN
      </button>
    </div>
  );

  return (
    <header className="sticky top-0 z-50 bg-white/85 backdrop-blur-xl border-b border-[#E2E8F0]">
      <div className="max-w-7xl mx-auto px-5 sm:px-6 h-[68px] flex items-center justify-between">
        <Link data-testid="nav-logo" to="/" className="flex items-center gap-3 group shrink-0" onClick={() => setOpen(false)}>
          <img src="/logo.png" alt="SportScience Academy" className="w-10 h-10 object-contain group-hover:-rotate-6 transition-transform duration-300" data-testid="nav-logo-image" />
          <span className="font-heading font-black text-[17px] text-[#0B1B3A] whitespace-nowrap">
            SportScience<span className="text-[#1D4ED8] ml-1">Academy</span>
          </span>
        </Link>

        <nav className="hidden xl:flex items-center gap-1 whitespace-nowrap mx-3 min-w-0 shrink text-sm overflow-hidden">{links}</nav>

        <div className="hidden xl:flex items-center gap-3 whitespace-nowrap shrink-0 pl-2">
          {adminLinks}
          {!user && (
            <Link data-testid="nav-courses-button" to="/courses" className="btn-blue !px-5 !py-2 text-sm">
              {t("nav.courses")}
            </Link>
          )}
          {langToggle()}
          {user ? (
            <>
              <Link to="/dashboard" data-testid="nav-user-name" className="text-sm font-semibold text-[#1D4ED8] max-w-[150px] truncate hover:underline">{user.name}</Link>
              <button data-testid="nav-logout-button" onClick={handleLogout} className="btn-outline-light !px-5 !py-2 text-sm">
                {t("nav.logout")}
              </button>
            </>
          ) : (
            <>
              <Link data-testid="nav-login-button" to="/login" className="text-sm font-bold text-[#334155] hover:text-[#1D4ED8] px-3 transition-colors duration-200">
                {t("nav.login")}
              </Link>
              <Link data-testid="nav-register-button" to="/register" className="btn-blue !px-5 !py-2 text-sm">
                {t("nav.register")}
              </Link>
            </>
          )}
        </div>

        <button data-testid="nav-mobile-menu-button" className="xl:hidden p-2 text-[#0B1B3A]" onClick={() => setOpen(!open)} aria-label="menu">
          {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>
      </div>

      {open && (
        <div className="xl:hidden bg-white border-t border-[#E2E8F0] px-5 py-5 flex flex-col gap-2">
          {links}
          <div className="flex flex-wrap items-center gap-3 pt-4 mt-2 border-t border-[#E2E8F0]">
            {langToggle("-mobile")}
            {user ? (
              <button data-testid="nav-logout-button-mobile" onClick={handleLogout} className="btn-outline-light !px-5 !py-2 text-sm">
                {t("nav.logout")}
              </button>
            ) : (
              <>
                <Link data-testid="nav-login-button-mobile" to="/login" onClick={() => setOpen(false)} className="btn-outline-light !px-5 !py-2 text-sm">
                  {t("nav.login")}
                </Link>
                <Link data-testid="nav-register-button-mobile" to="/register" onClick={() => setOpen(false)} className="btn-blue !px-5 !py-2 text-sm">
                  {t("nav.register")}
                </Link>
              </>
            )}
          </div>
        </div>
      )}
    </header>
  );
}
